import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import '../styles/design-system.css';
import '../styles/pages.css';

const IP = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = {
  Plus:   (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14" /></svg>),
  TagBig: (p) => (<svg width="40" height="40" {...IP} strokeWidth="1.5" {...p}><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>),
  Pencil: (p) => (<svg width="12" height="12" {...IP} {...p}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>),
  Trash:  (p) => (<svg width="12" height="12" {...IP} {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>),
};

const TAG_COLORS = [
  '#ff5436',
  '#8b9b3f',
  '#e8b94a',
  '#0d0d0f',
  '#c8d6a8',
  '#a8a8af',
  '#d63d20',
  '#6b6b72',
];

export default function TagsPage() {
  const { workspace } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: TAG_COLORS[0] });

  useEffect(() => {
    if (!workspace?.workspace_id) return;
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.workspace_id]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const { data: tagRows, error } = await supabase
        .from('tags')
        .select('*')
        .eq('workspace_id', workspace.workspace_id)
        .order('name');

      if (error) {
        console.warn('[tags] table read failed', error);
        toast.error(error.message || 'Failed to load tags');
        setTags([]);
        return;
      }

      const rows = tagRows ?? [];
      if (rows.length > 0) {
        setTags(rows);
        return;
      }

      const { data: links, error: linksError } = await supabase
        .from('links')
        .select('tags')
        .eq('workspace_id', workspace.workspace_id);
      if (linksError) {
        setTags([]);
        return;
      }
      const tagSet = {};
      (links || []).forEach((l) =>
        (l.tags || []).forEach((t) => {
          if (!tagSet[t]) tagSet[t] = { tag_id: t, name: t, color: TAG_COLORS[0], link_count: 0 };
          tagSet[t].link_count++;
        })
      );
      setTags(Object.values(tagSet));
    } catch {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      if (editing) {
        const { error } = await supabase
          .from('tags')
          .update({ name: form.name, color: form.color })
          .eq('tag_id', editing);
        if (error) throw error;
        toast.success('Tag updated');
        setEditing(null);
      } else {
        const tagId = `tag_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
        const { error } = await supabase.from('tags').insert({
          tag_id: tagId,
          workspace_id: workspace.workspace_id,
          name: form.name.trim(),
          color: form.color,
          link_count: 0,
        });
        if (error) throw error;
        toast.success('Tag created');
        setAdding(false);
      }
      setForm({ name: '', color: TAG_COLORS[0] });
      fetchTags();
    } catch (err) {
      toast.error(err.message || 'Failed to save tag');
    }
  };

  const handleDelete = async (tagId) => {
    try {
      const { error } = await supabase.from('tags').delete().eq('tag_id', tagId);
      if (error) throw error;
      setTags((t) => t.filter((x) => x.tag_id !== tagId));
      toast.success('Tag deleted');
    } catch {
      toast.error('Failed to delete tag');
    }
  };

  const startEdit = (tag) => {
    setEditing(tag.tag_id);
    setForm({ name: tag.name, color: tag.color || TAG_COLORS[0] });
    setAdding(true);
  };

  return (
    <div className="ds-page" data-testid="tags-page">
      <div className="page-header">
        <div>
          <div className="page-label">Library · {tags.length} tags</div>
          <h1 className="page-title">Tags.</h1>
          <p className="page-sub">Group links across folders for cross-cutting reports.</p>
        </div>
        <button
          type="button"
          className="btn-coral"
          onClick={() => { setAdding(true); setEditing(null); setForm({ name: '', color: TAG_COLORS[0] }); }}
        >
          <Icon.Plus /> New tag
        </button>
      </div>

      {adding && (
        <div className="settings-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 14 }}>{editing ? 'Edit tag' : 'New tag'}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <input
              type="text"
              style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 9, fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', outline: 'none' }}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder="e.g. campaign"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TAG_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  style={{ width: 28, height: 28, borderRadius: '50%', border: form.color === c ? '3px solid var(--ink)' : '2px solid var(--line)', backgroundColor: c, cursor: 'pointer', transition: 'all 0.15s', padding: 0, transform: form.color === c ? 'scale(1.1)' : 'scale(1)' }}
                  aria-label={`Select color ${c}`}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-coral" onClick={handleSave} disabled={!form.name.trim()}>{editing ? 'Update' : 'Create'}</button>
              <button type="button" className="btn-outline" onClick={() => { setAdding(false); setEditing(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="panel">
          <div className="ds-empty">
            <div className="ds-skeleton" style={{ height: 14, width: 160, marginBottom: 12 }} />
            <div className="ds-skeleton" style={{ height: 10, width: 240 }} />
          </div>
        </div>
      ) : tags.length === 0 ? (
        <div className="panel">
          <div className="ds-empty">
            <div className="ds-empty-icon">
              <Icon.TagBig style={{ width: 22, height: 22 }} />
            </div>
            <div className="ds-empty-title">No tags yet</div>
            <p className="ds-empty-sub">Create tags and apply them to links to stay organised.</p>
          </div>
        </div>
      ) : (
        <div className="panel" style={{ padding: 22 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {tags.map((tag) => {
              const color = tag.color || TAG_COLORS[0];
              return (
                <div
                  key={tag.tag_id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', background: `${color}10`, border: `1px solid ${color}30`, borderRadius: 8, cursor: 'default' }}
                >
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 12, color, fontWeight: 500, textTransform: 'lowercase' }}>
                    {tag.name}
                  </span>
                  <span style={{ fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 11, color: 'var(--ink-faint)', background: 'rgba(13,13,15,0.06)', borderRadius: 4, padding: '1px 6px' }}>
                    {tag.link_count || 0}
                  </span>
                  <button
                    type="button"
                    className="row-more"
                    style={{ width: 20, height: 20, fontSize: 12, color: 'var(--ink-faint)', border: 'none', background: 'transparent' }}
                    onClick={() => startEdit(tag)}
                    aria-label="Edit tag"
                  >
                    <Icon.Pencil />
                  </button>
                  <button
                    type="button"
                    className="row-more"
                    style={{ width: 20, height: 20, fontSize: 12, color: 'var(--coral)', border: 'none', background: 'transparent' }}
                    onClick={() => { if (window.confirm(`Delete tag "${tag.name}"?`)) handleDelete(tag.tag_id); }}
                    aria-label="Delete tag"
                  >
                    <Icon.Trash />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
